/**
 * Trade Route Connectivity Algorithm
 *
 * Uses BFS flood-fill from Kingdom (0,0) to find all tiles
 * connected via trade route segments.
 */

import { coordsToId, idToCoords, getAdjacentCoords } from '../../utils/hexGrid';

/**
 * Calculate which tiles are connected to Kingdom via trade routes
 * Uses BFS flood-fill starting from kingdom
 *
 * @param {Object} segments - Map of nodeId -> segment data { built: boolean }
 * @param {Object} discoveredNodes - Map of discovered tile data
 * @returns {{ connected: Set<string>, parentMap: Map<string, string> }}
 *   - connected: Set of nodeIds connected to kingdom
 *   - parentMap: Map of nodeId -> parentNodeId (for path toward Kingdom)
 */
export function calculateConnectedTiles(segments, discoveredNodes) {
  const connected = new Set();
  const parentMap = new Map(); // nodeId -> parentNodeId (toward Kingdom)

  // BFS queue - start from kingdom
  const queue = [{ q: 0, r: 0 }];
  const visited = new Set(['node_0_0', 'kingdom']);

  // Kingdom is always connected
  connected.add('kingdom');
  connected.add('node_0_0');

  while (queue.length > 0) {
    const current = queue.shift();
    const currentId = coordsToId(current.q, current.r);

    // Check all 6 adjacent hexes
    const neighbors = getAdjacentCoords(current.q, current.r);

    for (const neighbor of neighbors) {
      const nodeId = coordsToId(neighbor.q, neighbor.r);

      // Skip if already visited
      if (visited.has(nodeId)) continue;
      visited.add(nodeId);

      // Skip if tile not discovered
      if (!discoveredNodes[nodeId]) continue;

      // Check if this tile has a route segment
      if (segments[nodeId]?.built) {
        connected.add(nodeId);
        parentMap.set(nodeId, currentId);
        queue.push(neighbor);
      }
    }
  }

  return { connected, parentMap };
}

/**
 * Check if a specific tile is trade-enabled (connected to Kingdom via route)
 * @param {string} nodeId - Target node ID
 * @param {Set<string>} connectedTiles - Pre-computed connected set
 * @returns {boolean}
 */
export function isTileTradeEnabled(nodeId, connectedTiles) {
  if (nodeId === 'kingdom' || nodeId === 'node_0_0') return true;
  return connectedTiles.has(nodeId);
}

/**
 * Check if a tile can have a route segment built on it
 * (must be adjacent to existing route or kingdom)
 *
 * @param {string} targetNodeId - Target node ID
 * @param {Object} segments - Map of nodeId -> segment data
 * @param {Object} discoveredNodes - Map of discovered tile data
 * @returns {boolean}
 */
export function canBuildRouteSegment(targetNodeId, segments, discoveredNodes) {
  // Can't build on kingdom
  if (targetNodeId === 'kingdom' || targetNodeId === 'node_0_0') return false;

  // Must be discovered
  if (!discoveredNodes[targetNodeId]) return false;

  // Can't already have a segment
  if (segments[targetNodeId]?.built) return false;

  // Must be adjacent to existing route or kingdom
  const coords = idToCoords(targetNodeId);
  if (!coords) return false;

  const neighbors = getAdjacentCoords(coords.q, coords.r);
  return neighbors.some(n => {
    const nId = coordsToId(n.q, n.r);
    return nId === 'kingdom' ||
           nId === 'node_0_0' ||
           (n.q === 0 && n.r === 0) ||
           segments[nId]?.built;
  });
}
